// Today screen — design canvas variant B.
// The hero tracks feed + sleep in parallel; tapping the baby's name opens the
// Stage view. Sub-sheets (FeedSheet etc.) stay for advanced edit.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import type { BabyShellContext } from "./BabyShell";

import { Header } from "../components/Header";
import { DayRibbon } from "../components/DayRibbon";
import { TrackCards } from "../components/TrackCards";
import { DayTimeline } from "../components/DayTimeline";
import { GuidanceStrip } from "../components/GuidanceStrip";
import { Composer } from "../components/Composer";
import { OfflineBar } from "../components/OfflineBar";
import { LogSheet } from "../components/LogSheet";
import { AddEntrySheet } from "../components/AddEntrySheet";
import { DriftBanner } from "../components/DriftBanner";
import { PastDayView } from "../components/PastDayView";
import { MenuDropdown } from "../components/MenuDropdown";

import { FeedSheet } from "../components/sheets/FeedSheet";
import { DiaperSheet } from "../components/sheets/DiaperSheet";
import { PumpSheet } from "../components/sheets/PumpSheet";
import { NoteSheet } from "../components/sheets/NoteSheet";
import { WeightSheet } from "../components/WeightSheet";
import { WeightEntry } from "../components/WeightEntry";
import { StageSheet } from "../components/StageSheet";
import { EditEventSheet } from "../components/EditEventSheet";
import { ShareSheet } from "../components/ShareSheet";

import { useT } from "../lib/LocaleContext";
import { ageFromBirthday } from "../lib/age";
import { bandForWeeks } from "../data/tcbSchedule";
import { buildDayPlan, buildPastDayPlan } from "../lib/dayPlan";
import { detectDeviations, topHint } from "../lib/schedule";
import { isSameLocalDay } from "../lib/timeFmt";
import { lastFeedOz, lastNurseOz, lastWeight, todayTotals, type EventRow } from "../lib/events";

type SubSheet = "sleep" | "feed" | "diaper" | "pump" | "weight" | "weight-entry" | "note" | null;

export function Home() {
  const { baby, role, babyCount, events: eventsApi, updateBabyPhoto } = useOutletContext<BabyShellContext>();
  const { events, insert, update, remove, online, pendingCount } = eventsApi;
  const navigate = useNavigate();
  const t = useT();

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const refresh = () => setNow(new Date());
    const timer = window.setInterval(refresh, 30_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, []);

  const [params, setParams] = useSearchParams();
  const dayParam = params.get("day");
  const viewDate = useMemo(() => {
    if (!dayParam) return now;
    const d = new Date(`${dayParam}T12:00:00`);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  }, [dayParam, now]);

  const [showLog, setShowLog] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showStage, setShowStage] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [editEvent, setEditEvent] = useState<EventRow | null>(null);
  const [sub, setSub] = useState<SubSheet>(null);

  // Hints the user dismissed today. Scoped per-baby and per-day so a transient
  // alert (e.g. an overdue feed) can resurface tomorrow, but the same nudge
  // won't keep nagging once they've waved it off.
  const dismissKey = `babyhelper:dismissedHints:${baby.id}:${ymd(new Date())}`;
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(dismissKey);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });
  const dismissHint = useCallback(
    (id: string) => {
      setDismissedIds((prev) => {
        if (prev.includes(id)) return prev;
        const next = [...prev, id];
        try {
          localStorage.setItem(dismissKey, JSON.stringify(next));
        } catch {
          /* storage full / disabled — dismissal is best-effort */
        }
        return next;
      });
    },
    [dismissKey],
  );

  const referenceDate = baby.due_date ?? baby.birthday;
  const age = ageFromBirthday(referenceDate, viewDate);
  const band = bandForWeeks(age.weeks);

  const isToday = isSameLocalDay(viewDate, new Date());
  const plan = useMemo(
    () => (isToday ? buildDayPlan(events, band, now) : buildPastDayPlan(events, viewDate)),
    [events, band, viewDate, isToday, now],
  );

  // Two parallel tracks: the next feed and the next sleep. A live nap, when one
  // is running, owns the sleep track.
  const liveSleep =
    plan.events.find((e) => e.status === "live" && (e.kind === "sleep" || e.kind === "bedtime")) ?? null;
  const nextFeed =
    plan.events.find((e) => e.status === "upcoming" && (e.kind === "feed" || e.kind === "nurse")) ?? null;
  const nextSleep =
    plan.events.find((e) => e.status === "upcoming" && (e.kind === "sleep" || e.kind === "bedtime")) ?? null;

  const intake = useMemo(() => todayTotals(events), [events]);
  const napCount = useMemo(
    () =>
      plan.events.filter(
        (e) =>
          e.kind === "sleep" &&
          (e.status === "logged" || e.status === "live") &&
          // The tail of last night's sleep already counted on the day it began.
          !e.continuedFromPrevDay,
      ).length,
    [plan.events],
  );

  const handlePrevDay = useCallback(() => {
    const d = new Date(viewDate);
    d.setDate(d.getDate() - 1);
    if (ymd(d) < baby.birthday) return; // can't view a day before the baby was born
    setParams({ day: ymd(d) });
  }, [viewDate, baby.birthday, setParams]);

  const handleNextDay = useCallback(() => {
    if (isToday) return;
    const d = new Date(viewDate);
    d.setDate(d.getDate() + 1);
    if (isSameLocalDay(d, new Date())) setParams({});
    else setParams({ day: ymd(d) });
  }, [viewDate, isToday, setParams]);

  // Quick-log amounts: the shared per-baby default wins when set; otherwise
  // fall back to the last logged value.
  const quickFeedOz = baby.bottle_default_oz ?? lastFeedOz(events);
  const quickNurseOz = baby.nurse_default_oz ?? lastNurseOz(events);

  const onLogFeed = useCallback(async () => {
    await insert({ type: "feed", amount_oz: quickFeedOz });
  }, [quickFeedOz, insert]);

  const onStartNap = useCallback(async () => {
    // Never open a second sleep on top of a running one — that used to happen
    // after midnight, when the running sleep wasn't recognized as today's.
    if (events.some((e) => e.type === "sleep" && e.end_at === null)) return;
    const h = new Date().getHours();
    await insert({ type: "sleep", sleep_kind: h >= 19 || h < 7 ? "night" : "nap" });
  }, [events, insert]);

  const onEndNap = useCallback(async () => {
    const open = events.find((e) => e.type === "sleep" && e.end_at === null);
    if (open) await update(open.id, { end_at: new Date().toISOString() });
  }, [events, update]);

  const logWeight = useCallback(
    async (grams: number) => {
      await insert({ type: "weight", weight_grams: grams });
    },
    [insert],
  );

  // The single most important nudge: the top engine hint, banner-worthy only at
  // severity 1–2 (overdue feed, past wake window, over the day-sleep cap).
  const topGuidance = useMemo(() => {
    if (!isToday) return null;
    const hints = detectDeviations(events, band, baby, now, t).filter(
      (h) => !dismissedIds.includes(h.id),
    );
    const h = topHint(hints);
    return h && h.severity <= 2 ? h : null;
  }, [events, band, baby, isToday, t, dismissedIds, now]);

  return (
    <div className="bh-root">
      <Header
        babyName={baby.name}
        birthday={baby.birthday}
        dueDate={baby.due_date}
        events={events}
        viewDate={viewDate}
        photoDataUrl={baby.photo_data}
        onPhotoChange={updateBabyPhoto}
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        onMenu={() => setShowMenu(true)}
        onWeightChip={() => setSub(lastWeight(events) ? "weight" : "weight-entry")}
        onNameClick={() => setShowStage(true)}
      />

      <div className="bh-scroll" style={{ paddingBottom: isToday ? 172 : 24 }}>
        <OfflineBar online={online} pendingCount={pendingCount} />
        {isToday ? (
          <>
            {topGuidance && (
              <DriftBanner
                title={topGuidance.title}
                body={topGuidance.detail ?? ""}
                onDismiss={() => dismissHint(topGuidance.id)}
              />
            )}
            <DayRibbon plan={plan} activeId={liveSleep?.id ?? null} />
            <TrackCards
              liveSleep={liveSleep}
              nextFeed={nextFeed}
              nextSleep={nextSleep}
              intakeOz={intake.oz}
              feedCount={intake.feeds}
              sleepMinutes={intake.sleepMinutes}
              napCount={napCount}
              babyName={baby.name}
              band={band}
              onLogFeed={onLogFeed}
              onStartNap={onStartNap}
              onEndNap={onEndNap}
            />
            <GuidanceStrip events={events} band={band} now={now} />
            <DayTimeline
              plan={plan}
              events={events}
              onFullDay={() => navigate(`/b/${baby.id}/history`)}
              onEditEvent={setEditEvent}
            />
          </>
        ) : (
          <PastDayView
            plan={plan}
            events={events}
            viewDate={viewDate}
            onAddEntry={() => setShowAddEntry(true)}
            onEditEvent={setEditEvent}
          />
        )}
      </div>

      {isToday && (
        <Composer
          babyId={baby.id}
          events={events}
          insert={insert}
          update={update}
          onAddNap={() => setSub("sleep")}
          onOpenLog={() => setShowLog(true)}
        />
      )}

      <MenuDropdown
        open={showMenu}
        onClose={() => setShowMenu(false)}
        items={[
          ...(role === "creator" ? [{ label: t.menu.share, onClick: () => setShowShare(true) }] : []),
          { label: t.menu.weight, onClick: () => setSub("weight") },
          { label: t.menu.history, onClick: () => navigate(`/b/${baby.id}/history`) },
          { label: t.menu.settings, onClick: () => navigate(`/b/${baby.id}/settings`) },
          // Only when there's somewhere to switch to — otherwise it's a dead end.
          ...(babyCount > 1 ? [{ label: t.menu.switchBaby, onClick: () => navigate("/babies") }] : []),
        ]}
      />

      <LogSheet
        open={showLog}
        insert={insert}
        quickFeedOz={quickFeedOz}
        quickNurseOz={quickNurseOz}
        onClose={() => setShowLog(false)}
        onOpenSubsheet={(k) => {
          setShowLog(false);
          setSub(k);
        }}
      />

      <AddEntrySheet
        open={showAddEntry}
        viewDate={viewDate}
        quickFeedOz={quickFeedOz}
        quickNurseOz={quickNurseOz}
        insert={insert}
        onClose={() => setShowAddEntry(false)}
      />

      {sub === "sleep" && (
        <AddEntrySheet
          open
          napOnly
          viewDate={new Date()}
          quickFeedOz={quickFeedOz}
          quickNurseOz={quickNurseOz}
          insert={insert}
          onClose={() => setSub(null)}
        />
      )}

      <StageSheet
        open={showStage}
        babyName={baby.name}
        band={band}
        age={age}
        events={events}
        onClose={() => setShowStage(false)}
      />

      <ShareSheet
        open={showShare}
        babyId={baby.id}
        babyName={baby.name}
        onClose={() => setShowShare(false)}
      />

      <EditEventSheet
        event={editEvent}
        onSave={update}
        onDelete={remove}
        onClose={() => setEditEvent(null)}
      />

      <FeedSheet
        open={sub === "feed"}
        defaultOz={quickFeedOz}
        onSubmit={async (oz) => {
          await insert({ type: "feed", amount_oz: oz });
          setSub(null);
        }}
        onClose={() => setSub(null)}
      />
      <DiaperSheet
        open={sub === "diaper"}
        onSubmit={async (kind) => {
          await insert({ type: "diaper", diaper_kind: kind });
          setSub(null);
        }}
        onClose={() => setSub(null)}
      />
      <PumpSheet
        open={sub === "pump"}
        onSubmit={async (oz, durationMin) => {
          await insert({ type: "pump", amount_oz: oz, duration_min: durationMin });
          setSub(null);
        }}
        onClose={() => setSub(null)}
      />
      <WeightSheet
        open={sub === "weight"}
        events={events}
        babyName={baby.name}
        band={band}
        onLogWeight={logWeight}
        onClose={() => setSub(null)}
      />
      <WeightEntry
        open={sub === "weight-entry"}
        onSubmit={logWeight}
        onClose={() => setSub(null)}
      />
      <NoteSheet
        open={sub === "note"}
        title="Note"
        placeholder="Anything to remember?"
        onSubmit={async (text) => {
          await insert({ type: "note", note: text });
          setSub(null);
        }}
        onClose={() => setSub(null)}
      />
    </div>
  );
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

